import {ChemDrawStoichiometryData, ParseReactionComponentsResponse} from 'chemistry/ChemistryConstants';
import staticURL from 'lib/staticURL';
import {ExternalFile, FromServerNoteLinkedFile} from 'notebook/util/NoteUtilConstants';
import * as net from 'util/Net';

export const SALT_LIBRARY_URL = '/salts/listing';
export const SOLVENT_LIBRARY_URL = '/solvents/listing';
export const SALT_CREATE_FROM_CSV_URL = '/salts/create/bulk-csv';
export const SOLVENT_CREATE_FROM_CSV_URL = '/solvents/create/bulk-csv';

export const TEMPLATE_BULK_SALTS_CSV_LINK = staticURL('/static/chemistry/template_bulk_salts.csv');
export const TEMPLATE_BULK_SOLVENTS_CSV_LINK = staticURL('/static/chemistry/template_bulk_solvents.csv');

export function bulkFetchChemDrawStoichiometryData(
  noteLinkedObjectIds: Array<string>
): Promise<{[noteLinkedObjectId: string]: ChemDrawStoichiometryData}> {
  return net.postJSONPromise({
    url: '/1/api/chemistry:bulk-fetch-chemdraw-stoichiometry-data',
    data: {noteLinkedObjectIds},
  });
}

export function parseReactionComponents(mrvFile: string): Promise<ParseReactionComponentsResponse> {
  return net.postJSONPromise({
    url: '/1/api/reactions:process-components',
    data: {mrvFile},
  });
}

export function generateReactionImage(mrvFile: string): Promise<{base64SVG: string}> {
  return net.postJSONPromise({
    url: '/1/api/reactions:generate-image',
    data: {mrvFile},
  });
}

export interface BuildReactionFromComponentsResponse {
  externalFile: ExternalFile;
  noteLinkedFile: FromServerNoteLinkedFile;
}

export type ReactionComponentInput =
  | {type: 'molecule'; moleculeId: string}
  | {type: 'smiles'; smiles: string}
  | {type: 'molfile'; molfile: string};

export function buildReactionFromComponents(
  reactants: Array<ReactionComponentInput>,
  products: Array<ReactionComponentInput>
): Promise<BuildReactionFromComponentsResponse> {
  return net.postJSONPromise({
    url: '/1/api/reactions:build-from-components',
    data: {reactants, products},
  });
}

export function createSalt(name: string, smiles: string, code: string): Promise<void> {
  return net.postJSONPromise({
    url: '/1/api/salts',
    data: {name: name.trim(), smiles, code: code.trim()},
  });
}

export function createSolvent(name: string, smiles: string, code: string): Promise<void> {
  return net.postJSONPromise({
    url: '/1/api/solvents',
    data: {name: name.trim(), smiles, code: code.trim()},
  });
}

export function updateSalt(saltId: string, name: string, code: string): Promise<void> {
  return net.patchJSONPromise({
    url: `/1/api/salts/${saltId}`,
    data: {name: name.trim(), code: code.trim()},
  });
}

export function updateSolvent(solventId: string, name: string, code: string): Promise<void> {
  return net.patchJSONPromise({
    url: `/1/api/solvents/${solventId}`,
    data: {name: name.trim(), code: code.trim()},
  });
}

export function archiveSalts(saltIds: Array<string>, purpose: string): Promise<void> {
  return net.postJSONPromise({
    url: '/1/api/salts:archive',
    data: {saltIds, purpose},
  });
}

export function unarchiveSalts(saltIds: Array<string>): Promise<void> {
  return net.postJSONPromise({
    url: '/1/api/salts:unarchive',
    data: {saltIds},
  });
}

export function archiveSolvents(solventIds: Array<string>, purpose: string): Promise<void> {
  return net.postJSONPromise({
    url: '/1/api/solvents:archive',
    data: {solventIds, purpose},
  });
}

export function unarchiveSolvents(solventIds: Array<string>): Promise<void> {
  return net.postJSONPromise({
    url: '/1/api/solvents:unarchive',
    data: {solventIds},
  });
}

export interface ChemicalCounterpartCSVRow {
  name: string;
  smiles: string;
  code: string | null;
}

export interface ChemicalCounterpartRowValidationOutput {
  data: ChemicalCounterpartCSVRow;
  // Validation messages for this row; empty when the row is valid.
  errorMessages: Array<string>;
  isValid: boolean;
}

export interface ChemicalCounterpartCSVValidationOutput {
  fileValidationError: string | null;
  rowValidationOutputs: Array<ChemicalCounterpartRowValidationOutput> | null;
}

export function validateSaltsFromFile(
  filename: string,
  csvUrl: string
): Promise<ChemicalCounterpartCSVValidationOutput> {
  return net.postJSONPromise({
    url: '/1/api/salts:validate-csv',
    data: {csvUrl, filename},
  });
}

export function createSaltsFromFile(csvUrl: string, filename: string): Promise<{createdIds: Array<string>}> {
  return net.postJSONPromise({
    url: '/1/api/salts:bulk-create-from-csv',
    data: {csvUrl, filename},
  });
}

export function validateSolventsFromFile(
  filename: string,
  csvUrl: string
): Promise<ChemicalCounterpartCSVValidationOutput> {
  return net.postJSONPromise({
    url: '/1/api/solvents:validate-csv',
    data: {csvUrl, filename},
  });
}

export function createSolventsFromFile(
  csvUrl: string,
  filename: string
): Promise<{createdIds: Array<string>}> {
  return net.postJSONPromise({
    url: '/1/api/solvents:bulk-create-from-csv',
    data: {csvUrl, filename},
  });
}
